import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ListChecks, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  CalendarCheck,
  AlertCircle,
  CheckCircle2,
  Clock,
  Phone,
  ChevronRight,
  Sparkles,
  BedDouble
} from 'lucide-react';
import axios from 'axios';
import { fetchCensus, CENSUS_EVENT } from './Census';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parse a free-form shift time string ("10:00 AM", "2:00 PM", "23:00") and
// return the manual-shift duration in hours. Overnight wraps (end <= start)
// are added a 24-hour wrap so e.g. 11 PM → 7 AM = 8h. Returns 0 if either
// value can't be parsed (graceful — the KPI shouldn't throw).
const computeManualShiftHours = (start, end) => {
  if (!start || !end) return 0;
  const parse = (val) => {
    if (typeof val !== 'string') return null;
    const s = val.trim().toUpperCase().replace(/\./g, '');
    let m = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)$/);
    if (m) {
      let h = parseInt(m[1], 10);
      const mm = m[2] ? parseInt(m[2], 10) : 0;
      const ap = m[3];
      if (ap === 'PM' && h !== 12) h += 12;
      if (ap === 'AM' && h === 12) h = 0;
      return h + mm / 60;
    }
    m = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
    return null;
  };
  const a = parse(start);
  const b = parse(end);
  if (a === null || b === null) return 0;
  let h = b - a;
  if (h <= 0) h += 24;
  return Math.round(h * 10) / 10;
};

const Overview = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [weeklyShifts, setWeeklyShifts] = useState(null);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  // Adjacent weeks — needed so "On Shift Now" can cross week boundaries:
  //   • prev week: covers Sat-overnight when "now" is early Sunday morning (hour < 7)
  //   • next week: covers Up Next / Then when current shift is the last of this week
  const [prevWeekShifts, setPrevWeekShifts] = useState(null);
  const [nextWeekShifts, setNextWeekShifts] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  // Manual shifts for the CURRENT week. Used in the Utilization KPI so a
  // 4-hour manual shift contributes 4h (not 8h via the covered-slot proxy).
  const [manualShifts, setManualShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [censusBeds, setCensusBeds] = useState([]);

  // Live-refresh census whenever the Census page emits a change event,
  // when this tab regains focus, or when another tab writes (storage event).
  useEffect(() => {
    const refresh = async () => {
      try { setCensusBeds(await fetchCensus()); } catch { /* noop */ }
    };
    refresh();
    window.addEventListener(CENSUS_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(CENSUS_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const censusOccupied = censusBeds.filter((b) => b.occupied).length;
  const censusTotal = censusBeds.length;
  const censusPct = censusTotal > 0 ? Math.round((censusOccupied / censusTotal) * 100) : 0;

  // Fetch data
  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      // Fetch team members
      const teamResponse = await axios.get(`${API_URL}/api/team?active_only=true`);
      setTeamMembers(teamResponse.data);

      // Fetch current week shifts (week starts Sunday)
      const today = new Date();
      const sunday = getSunday(today);
      const weekStart = formatDate(sunday);
      const shiftsResponse = await axios.get(`${API_URL}/api/shifts/week/${weekStart}`);
      setWeeklyShifts(shiftsResponse.data);

      // Also fetch adjacent weeks so the "On Shift Now" card resolves shifts
      // across week boundaries (Sat-overnight → Sun-day, etc.)
      const prevSunday = new Date(sunday);
      prevSunday.setDate(prevSunday.getDate() - 7);
      const nextSunday = new Date(sunday);
      nextSunday.setDate(nextSunday.getDate() + 7);
      const [prevResp, nextResp] = await Promise.all([
        axios.get(`${API_URL}/api/shifts/week/${formatDate(prevSunday)}`),
        axios.get(`${API_URL}/api/shifts/week/${formatDate(nextSunday)}`),
      ]);
      setPrevWeekShifts(prevResp.data);
      setNextWeekShifts(nextResp.data);

      // Pull schedule annotations for the current week so they can be
      // surfaced as conflicts. Falls back to [] for non-authed users or stale
      // backends without the endpoint.
      try {
        const token = JSON.parse(localStorage.getItem('currentUser') || 'null')?.token;
        if (token) {
          const annResp = await axios.get(
            `${API_URL}/api/schedule/annotations/week/${weekStart}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setAnnotations(annResp.data || []);
        }
      } catch (e) {
        console.warn('Schedule annotations unavailable (stale backend?)', e);
        setAnnotations([]);
      }

      // Manual shifts for the same week. Fetched separately so the Utilization
      // KPI can sum actual durations rather than treating every covered slot
      // as 8h. Falls back to [] on error so the KPI degrades gracefully.
      try {
        const manResp = await axios.get(`${API_URL}/api/manual-shifts/week/${weekStart}`);
        setManualShifts(manResp.data || []);
      } catch (e) {
        console.warn('Manual shifts unavailable', e);
        setManualShifts([]);
      }

      // Generate recommendations from uncovered shifts
      generateRecommendations(shiftsResponse.data, teamResponse.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSunday = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay()); // 0 = Sunday → most recent Sunday
    return d;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const generateRecommendations = (weeklyData, team) => {
    const recs = [];
    const shiftLabels = {
      'day': 'Day (7am-3pm)',
      'swing': 'Swing (11am-7pm)',
      'evening': 'Evening (3pm-11pm)',
      'overnight': 'Overnight (11pm-7am)'
    };

    // Find uncovered required shifts
    weeklyData.daily_coverage.forEach((day, index) => {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const uncoveredRequired = day.shifts.filter(s => 
        !s.is_covered && ['day', 'evening', 'overnight'].includes(s.shift_type)
      );

      uncoveredRequired.forEach(shift => {
        // Find best staff for this shift
        const bestStaff = team.find(m => 
          m.preferred_shifts && m.preferred_shifts.includes(shift.shift_type)
        ) || team.find(m => 
          m.preferred_shifts && m.preferred_shifts.length === 4 // Flex
        );

        if (bestStaff && recs.length < 3) {
          recs.push({
            id: `rec-${recs.length}`,
            type: 'coverage',
            title: `Assign ${bestStaff.name} to ${dayNames[index]} ${shiftLabels[shift.shift_type]}`,
            description: `${bestStaff.name} has ${shift.shift_type} shift preference. Currently uncovered.`,
            action: 'Schedule',
            confidence: bestStaff.preferred_shifts.length === 4 ? 95 : 88,
            impact: 'Required shift coverage',
            date: day.date,
            shiftType: shift.shift_type,
            staffId: bestStaff.id
          });
        }
      });
    });

    setRecommendations(recs);
  };

  // Real phone numbers
  const phoneNumbers = {
    'Marvay Taylor': '(804) 363-7375',
    'Hurieya Morton': '(804) 947-3024',
    'Keisha Bailey': '(804) 668-6008'
  };

  // Map shift_type → human-friendly label using the "1st / 2nd / 3rd shift"
  // convention supervisors are used to seeing on the printed schedule.
  const SHIFT_LABEL = {
    day: '1st Shift (Day · 7AM–3PM)',
    evening: '2nd Shift (Evening · 3PM–11PM)',
    overnight: '3rd Shift (Overnight · 11PM–7AM)',
    swing: 'Swing (Optional)',
  };

  const formatConflictDate = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate KPIs
  const calculateKPIs = () => {
    if (!weeklyShifts) {
      return { fillRate: 0, utilization: 0, conflicts: 0, openShifts: 0, conflictsList: [] };
    }

    const totalRequired = weeklyShifts.daily_coverage.reduce((sum, day) => sum + day.required_shifts, 0);
    const totalCovered = weeklyShifts.daily_coverage.reduce((sum, day) => sum + day.covered_required, 0);
    const fillRate = Math.round((totalCovered / totalRequired) * 100);
    const openShifts = totalRequired - totalCovered;

    // === Staff Utilization KPI ===
    // Each STANDARD shift slot that is actually filled by a real assignment
    // (primary and/or secondary) contributes 8 hours. Manual-only coverage
    // (covered_by_manual=true with NO assigned_rss_id on the slot) does NOT
    // count here — the manual shift's actual duration is added below so the
    // KPI reflects real hours worked, not a phantom 8h.
    const SHIFT_HOURS = 8;
    let totalScheduledHours = 0;
    weeklyShifts.daily_coverage.forEach((day) => {
      day.shifts.forEach((s) => {
        if (s.assigned_rss_id) totalScheduledHours += SHIFT_HOURS;
        if (s.secondary_rss_id) totalScheduledHours += SHIFT_HOURS;
      });
    });
    // Add actual manual-shift durations (primary + secondary count as
    // separate staff-hour contributions, matching how standard secondaries
    // are treated above).
    (manualShifts || []).forEach((m) => {
      const hours = computeManualShiftHours(m.start_time, m.end_time);
      if (m.assigned_rss_id) totalScheduledHours += hours;
      if (m.secondary_rss_id) totalScheduledHours += hours;
    });
    const totalMaxHours = teamMembers.reduce((sum, m) => sum + (m.max_hours_per_week || 0), 0);
    const utilization = totalMaxHours > 0
      ? Math.round((totalScheduledHours / totalMaxHours) * 100)
      : 0;

    // Build the actionable conflicts list. Three sources are surfaced:
    //   1. "No coverage" — a required shift (Day/Evening/Overnight) is unfilled.
    //   2. "Preferred shift mismatch" — staff is assigned to a required shift
    //      that is NOT in their `preferred_shifts` list.
    //   3. Admin annotations from the Schedule grid:
    //      - requested_off  → "Requested Off"
    //      - needs_cover    → "Needs Cover"
    //      - swap_pending   → "Swap Pending"
    // The KPI count = length of this list, so the badge and the modal stay
    // in sync. Swing shifts are optional and never produce conflicts.
    const teamByName = new Map(teamMembers.map((m) => [m.name, m]));
    const conflictsList = [];
    weeklyShifts.daily_coverage.forEach((day) => {
      day.shifts.forEach((s) => {
        const isRequired = ['day', 'evening', 'overnight'].includes(s.shift_type);
        if (!isRequired) return;

        if (!s.is_covered) {
          conflictsList.push({
            id: `${day.date}-${s.shift_type}-uncovered`,
            date: day.date,
            dateLabel: formatConflictDate(day.date),
            shiftType: s.shift_type,
            shiftLabel: SHIFT_LABEL[s.shift_type] || s.shift_type,
            issueType: 'No coverage',
            staffName: null,
          });
          return;
        }

        const m = teamByName.get(s.assigned_rss_name);
        if (m && m.preferred_shifts && m.preferred_shifts.length > 0
            && !m.preferred_shifts.includes(s.shift_type)) {
          conflictsList.push({
            id: `${day.date}-${s.shift_type}-mismatch-${s.assigned_rss_name}`,
            date: day.date,
            dateLabel: formatConflictDate(day.date),
            shiftType: s.shift_type,
            shiftLabel: SHIFT_LABEL[s.shift_type] || s.shift_type,
            issueType: 'Preferred shift mismatch',
            staffName: s.assigned_rss_name,
          });
        }
      });
    });

    // Append admin annotations from the Schedule grid. completed_swap entries
    // are NOT treated as conflicts — they're informational shift-switch
    // history. We pass them through to the dialog separately.
    const ANNOTATION_ISSUE_LABEL = {
      requested_off: 'Requested Off',
      needs_cover:   'Needs Cover',
      swap_pending:  'Swap Pending',
    };
    const completedSwitches = [];
    (annotations || []).forEach((a) => {
      const baseShift = a.original_shift || null;
      const shiftLabel = baseShift
        ? (SHIFT_LABEL[baseShift] || baseShift)
        : 'Day Off';
      if (a.shift_type === 'completed_swap') {
        completedSwitches.push({
          id: `completed-swap-${a.id}`,
          date: a.date,
          dateLabel: formatConflictDate(a.date),
          shiftType: baseShift || 'off',
          shiftLabel,
          original: a.original_member_name,
          pickedUpBy: a.member_name,
          notes: a.notes || null,
        });
        return;
      }
      conflictsList.push({
        id: `annotation-${a.id}`,
        date: a.date,
        dateLabel: formatConflictDate(a.date),
        shiftType: baseShift || 'off',
        shiftLabel,
        issueType: ANNOTATION_ISSUE_LABEL[a.shift_type] || a.shift_type,
        staffName: a.shift_type === 'swap_pending'
          ? `${a.member_name} → ${a.swap_with}`
          : a.member_name,
      });
    });

    return {
      fillRate,
      utilization,
      conflicts: conflictsList.length,
      openShifts,
      conflictsList,
      completedSwitches,
    };
  };

  const kpis = calculateKPIs();

  // ==================== UPCOMING SHIFTS ====================
  // Returns 3 rows for the Overview card:
  //   [0] = currently on shift now
  //   [1] = next shift after that
  //   [2] = the shift after that
  // Sequence: Day (7-15) → Evening (15-23) → Overnight (23-7 next day) → repeat
  // No new data fetch — uses weeklyShifts + teamMembers already loaded.
  const upcomingShifts = () => {
    if (!weeklyShifts) return [];
    const now = new Date();
    const hour = now.getHours();

    // Build a map of date → daily_coverage for fast lookup.
    // Merge prev + current + next weeks so we can resolve shifts that fall on
    // an adjacent calendar day across either week boundary.
    const allCoverage = [
      ...(prevWeekShifts?.daily_coverage || []),
      ...weeklyShifts.daily_coverage,
      ...(nextWeekShifts?.daily_coverage || []),
    ];
    const byDate = new Map(allCoverage.map((d) => [d.date, d]));
    const dateStr = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    // SEQUENCE ORDER  ↓ shift type, time range, plus the date offset to apply
    // (overnight rolls into the *previous* day's record because shifts.week
    // creates an overnight slot dated to the day it starts)
    const SHIFT_ORDER = [
      { type: 'day',       time: '7:00 AM – 3:00 PM',  startHour: 7  },
      { type: 'evening',   time: '3:00 PM – 11:00 PM', startHour: 15 },
      { type: 'overnight', time: '11:00 PM – 7:00 AM', startHour: 23 },
    ];

    // Determine the index of the shift that is CURRENTLY active.
    let currentIdx;
    if (hour >= 7 && hour < 15) currentIdx = 0;        // Day
    else if (hour >= 15 && hour < 23) currentIdx = 1;  // Evening
    else currentIdx = 2;                                // Overnight (covers 23-7)

    // For overnight that starts at 23:00, the schedule record belongs to "today",
    // BUT if it's actually already past midnight (00:00-06:59), the record we want
    // is YESTERDAY's overnight slot (since the shift started the previous evening).
    const baseDate = new Date(now);
    baseDate.setHours(0, 0, 0, 0);
    if (hour < 7) baseDate.setDate(baseDate.getDate() - 1);

    // Walk forward 3 shifts starting from `currentIdx` on `baseDate`
    const rows = [];
    let dateCursor = new Date(baseDate);
    let idx = currentIdx;
    for (let i = 0; i < 3; i += 1) {
      const meta = SHIFT_ORDER[idx];
      const day = byDate.get(dateStr(dateCursor));
      const shift = day?.shifts?.find((s) => s.shift_type === meta.type);

      // Resolve the assigned staff. Prefer the denormalized name on the shift,
      // but fall back to looking up by `assigned_rss_id` against the roster —
      // this matches the Schedule page's lookup logic and prevents "Unassigned"
      // from showing when only the id is populated (legacy/reseeded data).
      let assignedName = shift?.assigned_rss_name || null;
      let member = assignedName
        ? teamMembers.find((m) => m.name === assignedName)
        : null;
      if (!assignedName && shift?.assigned_rss_id) {
        member = teamMembers.find((m) => m.id === shift.assigned_rss_id) || null;
        assignedName = member?.name || null;
      }

      rows.push({
        slot: i === 0 ? 'now' : i === 1 ? 'upnext' : 'then',
        shiftType: meta.type,
        timeRange: meta.time,
        date: dateStr(dateCursor),
        name: assignedName,
        staffType: (member?.staff_type || '').toUpperCase(),
      });

      // Advance to next shift in the sequence; roll the date forward when wrapping
      idx += 1;
      if (idx >= SHIFT_ORDER.length) {
        idx = 0;
        dateCursor.setDate(dateCursor.getDate() + 1);
      }
    }
    return rows;
  };

  const upcoming = upcomingShifts();

  // Get current week date range (timezone-safe: parse as local time, not UTC)
  const getWeekRange = () => {
    if (!weeklyShifts) return '';
    const start = new Date(weeklyShifts.week_start + 'T00:00:00');
    const end = new Date(weeklyShifts.week_end + 'T00:00:00');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          3.1 Supervised Living — Operations Dashboard
        </p>
      </div>

      {/* On Shift Now — 3 rows: Now / Up Next / Then */}
      <Card className="p-6" data-testid="on-shift-now-card">
        <div className="flex items-center gap-2 mb-4">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            On Shift Now
          </h2>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-y sm:divide-y-0 divide-border"
          data-testid="on-shift-rows"
        >
          {upcoming.map((row) => {
            const slotMeta = {
              now:    { label: 'Now',     pill: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300' },
              upnext: { label: 'Up Next', pill: 'bg-muted text-muted-foreground' },
              then:   { label: 'Then',    pill: 'bg-muted text-muted-foreground' },
            }[row.slot];
            const initials = row.name
              ? row.name.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()
              : '';
            return (
              <div
                key={row.slot}
                className="flex flex-col items-start gap-3 px-5 py-3 first:pl-0 last:pr-0 sm:py-0"
                data-testid={`on-shift-row-${row.slot}`}
              >
                <span
                  className={`inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap ${slotMeta.pill}`}
                >
                  {slotMeta.label}
                </span>

                <div className="flex items-center gap-3 min-w-0 w-full">
                  <div className={`h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    row.name
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {row.name ? initials : '—'}
                  </div>
                  <div className="min-w-0 flex-1">
                    {row.name ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold truncate" data-testid={`on-shift-name-${row.slot}`}>
                          {row.name}
                        </h3>
                        {row.staffType && (
                          <Badge variant="secondary" className="text-[10px]">
                            {row.staffType}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <h3
                        className="text-base font-semibold text-red-600 dark:text-red-400"
                        data-testid={`on-shift-name-${row.slot}`}
                      >
                        Unassigned
                      </h3>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  {row.timeRange}
                </p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Census */}
        <Card className="p-5" data-testid="kpi-census">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <BedDouble className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Census</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold" data-testid="kpi-census-value">
              {censusOccupied} / {censusTotal}
            </div>
            <div className="flex items-center gap-1 text-xs">
              {censusPct === 100 ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span className="text-green-600">Full house · {censusPct}%</span>
                </>
              ) : (
                <span className="text-muted-foreground">{censusPct}% occupied</span>
              )}
            </div>
          </div>
        </Card>

        {/* Open Shifts */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <CalendarCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Open Shifts</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.openShifts}</div>
            <div className="flex items-center gap-1 text-xs">
              {kpis.openShifts === 0 ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span className="text-green-600">Fully covered</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 text-red-600" />
                  <span className="text-red-600">Needs coverage</span>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Fill Rate */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Fill Rate</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.fillRate}%</div>
            <div className="flex items-center gap-1 text-xs">
              {kpis.fillRate >= 75 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-green-600">Good coverage</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-yellow-600" />
                  <span className="text-yellow-600">Needs attention</span>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Utilization */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Utilization</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.utilization}%</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Staff workload</span>
            </div>
          </div>
        </Card>

        {/* Conflicts — clickable, opens detail modal */}
        <button
          type="button"
          onClick={() => setConflictsOpen(true)}
          data-testid="kpi-conflicts"
          aria-label={`View ${kpis.conflicts} scheduling conflict${kpis.conflicts === 1 ? '' : 's'}`}
          className="rounded-xl border border-border/40 bg-card text-card-foreground shadow-soft p-5 text-left w-full hover:shadow-md hover:border-orange-300 transition cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Conflicts</h3>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold" data-testid="kpi-conflicts-value">
              {kpis.conflicts}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>{kpis.conflicts === 0 ? 'All clear' : 'Click to review'}</span>
            </div>
          </div>
        </button>
      </div>

      <ConflictsDialog
        open={conflictsOpen}
        onOpenChange={setConflictsOpen}
        conflicts={kpis.conflictsList}
        completedSwitches={kpis.completedSwitches}
        onGoToSchedule={() => {
          setConflictsOpen(false);
          navigate('/schedule');
        }}
      />

      {/* AI Recommendations */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Coverage Recommendations</h2>
          </div>
          <Badge variant="secondary" className="text-xs">Real-time</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Suggested assignments based on staff availability, shift preferences, and coverage gaps
        </p>

        <div className="space-y-3">
          {recommendations.length > 0 ? (
            recommendations.map((rec) => (
              <motion.div
                key={rec.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <h3 className="font-medium text-sm">{rec.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-xs">{rec.action}</Badge>
                      <span>{rec.confidence}% match</span>
                      <span>{rec.impact}</span>
                    </div>
                  </div>
                  <Button size="sm" className="ml-4">
                    Apply
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-600" />
              <p className="text-sm">All required shifts are covered!</p>
            </div>
          )}
        </div>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Capacity */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">RSS Staff Capacity</h2>
            <Badge variant="secondary">{teamMembers.length} active</Badge>
          </div>

          <div className="space-y-3">
            {teamMembers.map((member, index) => {
              // Calculate shift hours (mock for now - can be enhanced)
              const shiftsThisWeek = Math.floor(Math.random() * 4) + 1;
              const hoursPerShift = 8;
              const totalHours = shiftsThisWeek * hoursPerShift;
              const capacity = Math.round((totalHours / member.max_hours_per_week) * 100);

              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{member.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {member.staff_type.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              capacity >= 90 ? 'bg-red-600' :
                              capacity >= 75 ? 'bg-yellow-600' :
                              'bg-green-600'
                            }`}
                            style={{ width: `${Math.min(capacity, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {totalHours}h / {member.max_hours_per_week}h
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Card>

        {/* This Week Schedule */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">This Week Schedule</h2>
            <Badge variant="secondary">{getWeekRange()}</Badge>
          </div>

          {weeklyShifts && (
            <div className="space-y-3">
              {weeklyShifts.daily_coverage.map((day, index) => {
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const date = new Date(day.date + 'T00:00:00');
                const isToday = formatDate(new Date()) === day.date;

                return (
                  <div key={day.date} className={`p-3 rounded-lg border ${isToday ? 'ring-2 ring-primary' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm">{dayNames[index]}</h3>
                        <span className="text-xs text-muted-foreground">
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        {isToday && <Badge variant="default" className="text-xs">Today</Badge>}
                      </div>
                      <div className={`text-sm font-bold ${
                        day.fill_rate >= 100 ? 'text-green-600' :
                        day.fill_rate >= 66 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {day.fill_rate}%
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {day.covered_required}/{day.required_shifts} required shifts covered
                    </div>
                    {day.uncovered_required > 0 && (
                      <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {day.uncovered_required} uncovered
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Quick Contacts */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Key Contacts</h2>
          <Badge variant="secondary">3.1 Program</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {teamMembers.filter(m => phoneNumbers[m.name]).map((member) => (
            <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg border">
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{member.name}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3" />
                  <span>{phoneNumbers[member.name]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 3.1 Tasks & Compliance */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Action Items</h2>
          <Badge variant="secondary">DBHDS Compliance</Badge>
        </div>

        <div className="space-y-3">
          <div className="p-4 border rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="destructive" className="text-xs">High</Badge>
                  <h3 className="font-medium text-sm">Complete Overnight Shift Coverage</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {kpis.openShifts} uncovered overnight shifts need immediate assignment
                </p>
                <div className="text-xs text-muted-foreground">Due: This week</div>
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="text-xs bg-yellow-600">Medium</Badge>
                  <h3 className="font-medium text-sm">DBHDS Monthly Documentation Review</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Review and submit monthly compliance documentation to DBHDS
                </p>
                <div className="text-xs text-muted-foreground">Due: End of month</div>
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">Low</Badge>
                  <h3 className="font-medium text-sm">Staff Training Certifications</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  3 staff members need CPR/First Aid recertification
                </p>
                <div className="text-xs text-muted-foreground">Due: Next month</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ==================== CONFLICTS DIALOG ====================
// Surfaces the same list of conflicts that backs the Conflicts KPI count.
// Each row links straight to the Schedule page so a supervisor can fix the
// issue with one click. "No coverage" rows are highlighted as the most
// urgent (red), preferred-shift mismatches are flagged as warnings (amber).
const ConflictsDialog = ({ open, onOpenChange, conflicts, completedSwitches = [], onGoToSchedule }) => {
  const groups = conflicts.reduce((acc, c) => {
    (acc[c.date] = acc[c.date] || []).push(c);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort();
  const switchGroups = completedSwitches.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const switchDates = Object.keys(switchGroups).sort();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="conflicts-dialog">
        <DialogHeader>
          <DialogTitle>Scheduling Conflicts</DialogTitle>
          <DialogDescription>
            {conflicts.length === 0
              ? 'Your weekly coverage is fully compliant.'
              : `${conflicts.length} issue${conflicts.length === 1 ? '' : 's'} need attention this week.`}
          </DialogDescription>
        </DialogHeader>

        {conflicts.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="conflicts-empty"
          >
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
            <p className="text-base font-medium">No scheduling conflicts detected.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Every required shift is covered and assigned to preferred staff.
            </p>
          </div>
        ) : (          <div className="space-y-4">
            {dates.map((date) => (
              <div key={date}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {groups[date][0].dateLabel}
                </h4>
                <div className="space-y-2">
                  {groups[date].map((c) => {
                    const isCritical = c.issueType === 'No coverage';
                    // "Swap Pending" is informational, not a conflict to fix —
                    // give it a neutral blue treatment instead of amber.
                    const isInfo = c.issueType === 'Swap Pending';
                    return (
                      <div
                        key={c.id}
                        data-testid={`conflict-row-${c.id}`}
                        className={`p-3 rounded-lg border flex items-start justify-between gap-3 ${
                          isCritical
                            ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
                            : isInfo
                            ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30'
                            : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30'
                        }`}
                      >
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              variant={isCritical ? 'destructive' : isInfo ? 'default' : 'secondary'}
                              className="text-[10px]"
                              data-testid={`conflict-issue-${c.id}`}
                            >
                              {c.issueType}
                            </Badge>
                            <span className="text-sm font-medium">{c.shiftLabel}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {c.staffName ? (
                              <>Staff involved: <span className="font-medium text-foreground">{c.staffName}</span></>
                            ) : (
                              <>No staff assigned to this required shift.</>
                            )}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={onGoToSchedule}
                          className="shrink-0"
                          data-testid={`conflict-go-${c.id}`}
                        >
                          Go to Schedule
                          <ChevronRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== COMPLETED SHIFT SWITCHES =====
            Informational history of RSS-initiated swaps that have been
            recorded. Not a coverage problem — rendered separately under its
            own header so leadership can audit movement without it inflating
            the conflict count. */}
        {switchDates.length > 0 && (
          <div className="space-y-3 mt-2" data-testid="completed-switches-section">
            <div className="flex items-center gap-2 border-t pt-4">
              <h3 className="text-sm font-semibold">Completed Shift Switches</h3>
              <Badge variant="secondary" className="text-[10px]">Informational</Badge>
            </div>
            {switchDates.map((date) => (
              <div key={date}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {switchGroups[date][0].dateLabel}
                </h4>
                <div className="space-y-2">
                  {switchGroups[date].map((s) => (
                    <div
                      key={s.id}
                      data-testid={`completed-switch-row-${s.id}`}
                      className="p-3 rounded-lg border border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30 flex items-start justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            className="text-[10px] bg-violet-600 hover:bg-violet-600"
                            data-testid={`completed-switch-label-${s.id}`}
                          >
                            Shift Switch
                          </Badge>
                          <span className="text-sm font-medium">{s.shiftLabel}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{s.original}</span>
                          {' switched with '}
                          <span className="font-medium text-foreground">{s.pickedUpBy}</span>
                        </p>
                        {s.notes && (
                          <p className="text-xs italic text-muted-foreground">{s.notes}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={onGoToSchedule}
                        className="shrink-0"
                        data-testid={`completed-switch-go-${s.id}`}
                      >
                        View Schedule
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Overview;
